import { Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import './App.css';
import AuthWrapper from './Auth.js';
import { Sidebar } from './components/SideBar/Sidebar.js';
import { TopBarSignedIn } from './components/TopBars/TopBarSignedIn.js';
import { EvScreen } from './pages/evscreen/EVScreen/evScreen.js';
import { useEffect, useState, useCallback } from 'react';
import { Verify } from './pages/verify/Verify.js';
import { useAuth0 } from '@auth0/auth0-react';
import { Landing } from './pages/landing/Landing.js';
import axios from 'axios';
import ww_logogif from './assets/ww_loading2.gif';
import { OnboardingFlow } from './pages/onboarding/OnboardingFlow.js';
import { TopBarLanding } from './components/TopBars/TopBarLanding.js';
import { Payment } from './pages/payment/Payment.js';
import { SidebarMobile } from './components/SideBar/SidebarMobile.js';
import { Footer } from './components/Footer/Footer.js';
import { Arbitrage } from './pages/arbitrage/Arbitrage.js';
import { OutlierPlays } from './pages/outlier_tool/OutlierPlays.js';
import { Screener2 } from './pages/screener/Screener2.js';
import { SignUp } from './pages/landing/SignUp/SignUp.js';
import { SessionExpiredModal } from './components/SessionExpiredModal/SessionExpiredModal';
import { FreeEvScreen } from './pages/evscreen/FreeEvScreen/FreeEvScreen.js';
import { isPaidPlan } from './utils/isPaidPlan.js';
import { BlogIndex } from './pages/blog/BlogIndex.js';
import { BlogPost } from './pages/blog/BlogPost.js';
import { ToolsIndex } from './pages/tools/ToolsIndex.js';
import { ToolPage } from './pages/tools/ToolPage.js';

const scrollToRegion = () => {
	const hash = window.location.hash;
	if (hash) {
		const element = document.querySelector(hash);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	}
};

function App() {
	let user_local = JSON.parse(window.localStorage.getItem('userdb'));
	const userbankroll = user_local?.bankroll;
	const [stripe_status, setStripeStatus] = useState('');
	const [accessToken, setAccessToken] = useState('');
	//
	const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
	//
	const [filtersOpen, setFiltersOpen] = useState();
	const [isSigningUp, setIsSigningUp] = useState(false);

	const [width, setWidth] = useState(window.innerWidth);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 750);
	const [isSessionExpired, setIsSessionExpired] = useState(false);
	const [isSessionConflict, setIsSessionConflict] = useState(false);

	// Handler for session conflicts
	const handleSessionConflict = useCallback((errorData) => {
		// Clear all authentication state
		window.localStorage.clear();
		sessionStorage.clear();
		
		// Set session conflict state to show modal
		setIsSessionConflict(true);
		setIsSessionExpired(false); // Ensure session expired is false
	}, []); // No dependencies needed as state setters are stable

	// Set up global axios interceptor to catch 403 errors and session conflicts
	useEffect(() => {
		const interceptor = axios.interceptors.response.use(
			(response) => response,
			(error) => {
				// Check for session conflict (401 with forceReauth and SESSION_CONFLICT code)
				if (error.response?.status === 401) {
					const errorData = error.response.data;
					if (errorData?.forceReauth === true && errorData?.code === 'SESSION_CONFLICT') {
						handleSessionConflict(errorData);
						return Promise.reject(error);
					}
				}
				
				// Handle 403 errors (session expired) from API calls
				if (error.response?.status === 403) {
					setIsSessionExpired(true);
					setIsSessionConflict(false); // Ensure session conflict is false
					window.localStorage.clear();
				}
				return Promise.reject(error);
			}
		);

		// Cleanup interceptor on unmount
		return () => {
			axios.interceptors.response.eject(interceptor);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // setIsSessionExpired and setIsSessionConflict are stable, no need to include in deps

	useEffect(() => {
		const handleResize = () => {
			setWidth(window.innerWidth);
			if (window.innerWidth >= 750) {
				setIsMobile(false);
			} else {
				setIsMobile(true);
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const setaccesstoken = async () => {
			try {
				const token = await getAccessTokenSilently();
				setAccessToken(token);

				localStorage.setItem('access_token', token);
			} catch (e) {
				// Check for session conflict (401 with forceReauth and SESSION_CONFLICT code)
				if (e.response?.status === 401) {
					const errorData = e.response.data;
					if (errorData?.forceReauth === true && errorData?.code === 'SESSION_CONFLICT') {
						handleSessionConflict(errorData);
						setAccessToken(null);
						return;
					}
				}
				
				// Check if it's a 403 error (session expired)
				const is403Error = 
					e.status === 403 || 
					e.error === 'access_denied' ||
					e.error === 'invalid_grant' ||
					e.message?.includes('403') ||
					(e.response && e.response.status === 403);
				
				if (is403Error) {
					setIsSessionExpired(true);
					setIsSessionConflict(false);
					window.localStorage.clear();
				}
				setAccessToken(null);
			}
		};
		if (accessToken === '') {
			setaccesstoken();
		}
	});

	useEffect(() => {
		const getUserFromDB = async () => {
			try {
				const token = await getAccessTokenSilently();

				const userfromdb = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user?email=${user?.email}`, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				});
				//
				if (userfromdb.data) {
					setStripeStatus(userfromdb?.data?.stripe_plan_active || '');
				}
			} catch (e) {
				// Check for session conflict (401 with forceReauth and SESSION_CONFLICT code)
				if (e.response?.status === 401) {
					const errorData = e.response.data;
					if (errorData?.forceReauth === true && errorData?.code === 'SESSION_CONFLICT') {
						handleSessionConflict(errorData);
						return;
					}
				}
				
				// Check if it's a 403 error (session expired) from Auth0 token refresh
				const is403Error = 
					e.status === 403 || 
					e.error === 'access_denied' ||
					e.error === 'invalid_grant' ||
					e.message?.includes('403') ||
					(e.response && e.response.status === 403);
				
				// Show session expired modal for 403 errors or other authentication-related error status codes
				if (is403Error || e.response?.status >= 400) {
					setIsSessionExpired(true);
					setIsSessionConflict(false);
					window.localStorage.clear(); // Clear all local storage
				}
			}
		};

		if (accessToken !== '' && user) {
			window.localStorage.setItem('auth0user', JSON.stringify(user));
			getUserFromDB();
		}
	}, [accessToken, user]);

	const [bankroll, setBankroll] = useState(userbankroll || 10000);
	const [introCompleted, setIntroCompleted] = useState(true);

	const pathname = window.location.pathname;
	const queryParams = new URLSearchParams(window.location.search);
	const iValue = queryParams.get('i');
	const [discordVerified, setDiscordVerified] = useState(false);
	const [isEvReady, setIsEvReady] = useState(false);

	useEffect(() => {
		const registerUserLocally = async () => {
			if (isAuthenticated && user) {
				try {
					const token = await getAccessTokenSilently();
					//

					// Send user data to your backend
					const u = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/user`, user, {
						headers: {
							Authorization: `Bearer ${token}`,
						},
					});
					//
					window.localStorage.setItem('userdb', JSON.stringify(u.data));
					setIsEvReady(true);

					if (u.data.intro_completed && u.data.intro_completed !== 'false') {
						setIntroCompleted(true);
					} else if (!u.data.intro_completed || u.data.intro_completed === 'false') {
						setIntroCompleted(false);
					}
					setStripeStatus(u?.data?.stripe_plan.active || '');
					setDiscordVerified(u?.data?.discord_verified);
					//

					//
				} catch (error) {
					// Check for session conflict (401 with forceReauth and SESSION_CONFLICT code)
					if (error.response?.status === 401) {
						const errorData = error.response.data;
						if (errorData?.forceReauth === true && errorData?.code === 'SESSION_CONFLICT') {
							handleSessionConflict(errorData);
							return;
						}
					}
					
					// Check if it's a 403 error (session expired) from Auth0 token refresh
					const is403Error = 
						error.status === 403 || 
						error.error === 'access_denied' ||
						error.error === 'invalid_grant' ||
						error.message?.includes('403') ||
						(error.response && error.response.status === 403);
					
					// Show session expired modal for 403 errors or other authentication-related error status codes
					if (is403Error || error.response?.status >= 400) {
						setIsSessionExpired(true);
						setIsSessionConflict(false);
						window.localStorage.clear(); // Clear all local storage
					}
				}
			}
		};

		registerUserLocally();
	}, [isAuthenticated, user, getAccessTokenSilently]);

	useEffect(() => {
		// update if the user is verified based on the I value
	});

	useEffect(() => {
		const loginFn = async () => {
			try {
				const accessToken = window.localStorage.getItem('access_token');
				const email = JSON.parse(window.localStorage.getItem('userdb')).email;

				// verify the discord user
				const verify = await axios.post(
					`${process.env.REACT_APP_BACKEND_URL}/verify?i=${iValue}&email=${email}`,
					{},
					{
						headers: {
							Authorization: `Bearer ${accessToken}`,
						},
					}
				);

				//
				if (verify.data) {
					setDiscordVerified(true);
				} else {
					setDiscordVerified(false);
				}
			} catch (error) {
				// Check for session conflict (401 with forceReauth and SESSION_CONFLICT code)
				if (error.response?.status === 401) {
					const errorData = error.response.data;
					if (errorData?.forceReauth === true && errorData?.code === 'SESSION_CONFLICT') {
						handleSessionConflict(errorData);
						return;
					}
				}
				
				// Show session expired modal for any authentication-related error status codes
				if (error.response?.status >= 400) {
					setIsSessionExpired(true);
					setIsSessionConflict(false);
					window.localStorage.clear(); // Clear all local storage
				}
			}
		};
		if (iValue !== null) {
			loginFn();
		}
	});

	return (
		<div
			className="font"
			style={{ minHeight: '100vh' }}>
			<SessionExpiredModal 
				isOpen={isSessionExpired || isSessionConflict} 
				isSessionConflict={isSessionConflict}
				onClose={() => {
					setIsSessionExpired(false);
					setIsSessionConflict(false);
				}}
			/>
			{!isLoading && (
				<>
					{isAuthenticated && (
						<>
							{!introCompleted && introCompleted !== 'false' && (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										height: '100dvh',
										maxHeight: '100dvh',
										overflow: 'hidden',
									}}>
									<TopBarSignedIn isMobile={isMobile} bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />
									<div className="flex flex-row flex-1 min-h-0 overflow-hidden">
										{!isMobile && <Sidebar />}
										<div
											style={{
												flex: 1,
												width: '100%',
												minWidth: 0,
												minHeight: 0,
												overflow: 'hidden',
												display: 'flex',
												flexDirection: 'column',
											}}>
											<OnboardingFlow bankroll={bankroll} setBankroll={setBankroll} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} setIsSessionExpired={setIsSessionExpired} />
										</div>
									</div>
								</div>
							)}
							{pathname === '/feeds/verify' && <Verify />}
							{pathname === '/' && introCompleted && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#141416' }}>
									<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									<div style={{ display: 'flex', flex: 1 }}>
										{!isMobile && <Sidebar />}
										<div style={{ 
											flex: 1,
											// width: '116vw',
											// scale: '0.85',
											// transformOrigin: 'top left',
										}}>
											{isEvReady && (
												isPaidPlan(user_local) ? (
													<EvScreen isMobile={isMobile} bankroll={bankroll} setBankroll={setBankroll} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />
												) : (
													<FreeEvScreen isMobile={isMobile} hideSignupBanner={true} />
												)
											)}
										</div>
									</div>
									<Footer />
								</div>
							)}
							{/* {pathname === '/pricing' && (
								<Payment isMobile={isMobile} isAuthenticated={isAuthenticated} bankroll={bankroll} setBankroll={setBankroll} setFiltersOpen={setFiltersOpen} user={user} filtersOpen={filtersOpen} />
							)} */}
						</>
					)}
					{/* <AuthWrapper> */}

					{/* </AuthWrapper> */}
					{!isAuthenticated && pathname === '/' && (
						<>
							<div>
								<Landing login={() => {}} />
							</div>
						</>
					)}
					{pathname === '/pricing' && <Payment isAuthenticated={isAuthenticated} isMobile={isMobile} user={user} />}
					{pathname === '/arbitrage' && introCompleted && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'hidden' }}>
									<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									<div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
										{!isMobile && <Sidebar />}
										<div style={{ 
											flex: 1,
											width: '100%',
											overflow: 'auto',
											position: 'relative',
											zIndex: 1
										}}>
											{isEvReady && (
												<Arbitrage
													isMobile={isMobile}
													accessToken={accessToken}
													email={user?.email}
													bankroll={bankroll}
													setBankroll={setBankroll}
													filtersOpen={filtersOpen}
													setFiltersOpen={setFiltersOpen}
												/>
											)}
										</div>
									</div>
									<Footer />
								</div>
							)}
					{pathname === '/outliers' && introCompleted && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
									<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									<div style={{ display: 'flex', flex: 1 }}>
										{!isMobile && <Sidebar />}
										<div style={{ 
											flex: 1,
											width: '100%'
										}}>
											{isEvReady && (
												<OutlierPlays
													isMobile={isMobile}
													accessToken={accessToken}
													email={user?.email}
													bankroll={bankroll}
													setBankroll={setBankroll}
													filtersOpen={filtersOpen}
													setFiltersOpen={setFiltersOpen}
												/>
											)}
										</div>
									</div>
									<Footer />
								</div>
							)}

					{pathname === '/screener' && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'hidden' }}>
									{isAuthenticated ? (
										<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									) : (
										<TopBarLanding login={() => {}} setIsSigningUp={setIsSigningUp} isSigningUp={isSigningUp} scrollToRegion={scrollToRegion} />
									)}
									<div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
										{isAuthenticated && !isMobile && <Sidebar />}
										<div
											className="screener-main-scroll"
											style={{
												flex: 1,
												width: '100%',
												minWidth: 0,
												boxSizing: 'border-box',
												overflowY: 'auto',
												scrollbarGutter: 'stable',
											}}>
											<Screener2
												isMobile={isMobile}
												accessToken={accessToken}
												email={user?.email}
												bankroll={bankroll}
												setBankroll={setBankroll}
												filtersOpen={filtersOpen}
												setFiltersOpen={setFiltersOpen}
											/>
										</div>
									</div>
									<Footer />
								</div>
							)}

					{pathname === '/ev-scanner' && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#141416' }}>
									{isAuthenticated ? (
										<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									) : (
										<TopBarLanding login={() => {}} setIsSigningUp={setIsSigningUp} isSigningUp={isSigningUp} scrollToRegion={scrollToRegion} />
									)}
									<div style={{ display: 'flex', flex: 1 }}>
										{isAuthenticated && !isMobile && <Sidebar />}
										<div style={{ flex: 1, width: '100%' }}>
											<FreeEvScreen isMobile={isMobile} />
										</div>
									</div>
									<Footer />
								</div>
							)}

				{pathname === '/learn' && (() => { window.location.replace('/blog'); return null; })()}

					{pathname === '/blog' && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#141416' }}>
									{isAuthenticated ? (
										<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									) : (
										<TopBarLanding login={() => {}} setIsSigningUp={setIsSigningUp} isSigningUp={isSigningUp} scrollToRegion={scrollToRegion} />
									)}
									{isSigningUp && <SignUp login={() => {}} />}
									{!isSigningUp && (
										<div style={{ display: 'flex', flex: 1 }}>
											{isAuthenticated && !isMobile && <Sidebar />}
											<div style={{ flex: 1, width: '100%' }}>
												<BlogIndex />
											</div>
										</div>
									)}
									<Footer />
								</div>
							)}

					{pathname.startsWith('/blog/') && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#141416' }}>
									{isAuthenticated ? (
										<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									) : (
										<TopBarLanding login={() => {}} setIsSigningUp={setIsSigningUp} isSigningUp={isSigningUp} scrollToRegion={scrollToRegion} />
									)}
									{isSigningUp && <SignUp login={() => {}} />}
									{!isSigningUp && (
										<div style={{ display: 'flex', flex: 1 }}>
											{isAuthenticated && !isMobile && <Sidebar />}
											<div style={{ flex: 1, width: '100%', overflowY: 'auto' }}>
												<BlogPost />
											</div>
										</div>
									)}
									<Footer />
								</div>
							)}

					{pathname === '/tools' && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#141416' }}>
									{isAuthenticated ? (
										<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									) : (
										<TopBarLanding login={() => {}} setIsSigningUp={setIsSigningUp} isSigningUp={isSigningUp} scrollToRegion={scrollToRegion} />
									)}
									{isSigningUp && <SignUp login={() => {}} />}
									{!isSigningUp && (
										<div style={{ display: 'flex', flex: 1 }}>
											{isAuthenticated && !isMobile && <Sidebar />}
											<div style={{ flex: 1, width: '100%' }}>
												<ToolsIndex />
											</div>
										</div>
									)}
									<Footer />
								</div>
							)}

					{pathname.startsWith('/tools/') && (
								<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#141416' }}>
									{isAuthenticated ? (
										<TopBarSignedIn bankroll={bankroll} setBankroll={setBankroll} email={user?.email} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} isMobile={isMobile} />
									) : (
										<TopBarLanding login={() => {}} setIsSigningUp={setIsSigningUp} isSigningUp={isSigningUp} scrollToRegion={scrollToRegion} />
									)}
									{isSigningUp && <SignUp login={() => {}} />}
									{!isSigningUp && (
										<div style={{ display: 'flex', flex: 1 }}>
											{isAuthenticated && !isMobile && <Sidebar />}
											<div style={{ flex: 1, width: '100%', overflowY: 'auto' }}>
												<ToolPage />
											</div>
										</div>
									)}
									<Footer />
								</div>
							)}
				</>
			)}
			{isLoading && (
				<div style={{ justifySelf: 'center' }}>
					<img style={{ marginTop: '40px' }} width={300} src={ww_logogif} alt="loading icon" />
				</div>
			)}
		</div>
	);
}

export default App;
